import type { Market } from '@bridebook/toolbox/src/gazetteer';
import { PricingAccordion } from '@bridebook/ui';
import { EPricingDealType } from '@bridebook/ui/src/components/pricing-accordion/types';
import { useTranslation } from 'next-i18next';
import { type ComponentProps, type ReactNode, useMemo } from 'react';

interface IProps {
  dealType: EPricingDealType;
  initiallyExpanded?: boolean;
  guests?: number;
  icon?: ReactNode;
  includedInPricing: string[];
  info?: string;
  label?: string;
  labelInfo?: string;
  guestsInfo?: ReactNode;
  market: Market;
  maxPrice: number;
  minPrice: number;
  supplierCurrency: string | null;
  isMobile: boolean;
  onShowDetailsClick?: (label: string) => void;
}
const PricingAccordionRenderer = ({
  dealType,
  initiallyExpanded,
  icon,
  includedInPricing,
  info,
  label = '',
  guestsInfo,
  market,
  maxPrice,
  minPrice,
  supplierCurrency,
  isMobile,
  onShowDetailsClick,
}: IProps) => {
  const { t } = useTranslation('supplier');
  const texts: ComponentProps<typeof PricingAccordion>['texts'] = useMemo(
    () => ({
      includedInPricingTitle: t('supplier.pricing.includedInPricing.title'),
      showDetailsCta: t('supplier.packages.showDetails'),
      hideDetailsCta: t('supplier.packages.hideDetails'),
    }),
    [t],
  );

  const currency = supplierCurrency || market.currency;

  const priceLabel = useMemo(() => {
    if (maxPrice) {
      return market.formatCurrency([minPrice, maxPrice], { currency });
    }

    return t('supplier.venue.pricing.priceFrom', {
      minPrice: market.formatCurrency(minPrice, { currency }),
    });
  }, [currency, market, maxPrice, minPrice, t]);

  return (
    <PricingAccordion
      texts={texts}
      dealType={dealType}
      icon={icon}
      info={info}
      includedInPricing={includedInPricing}
      initiallyExpanded={initiallyExpanded}
      label={label}
      priceLabel={priceLabel}
      guestsInfo={guestsInfo}
      isMobile={isMobile}
      onShowDetailsClick={onShowDetailsClick}
    />
  );
};

export default PricingAccordionRenderer;
